import { useMemo } from 'react';
import { AddressType, AddressTypes, IOriginAddressInternet } from '../AddressManualEntry';
import { IOriginAddressMeter } from '../fetchers';
import { stripNMIChecksum } from '../helpers';
import { SalesStructuredAddress } from '../models';
import { AddressLOCSearch } from './AddressLOCSearch';
import { AddressMIRNSearch } from './AddressMIRNSearch';
import { AddressNMISearch } from './AddressNMISearch';
import { AddressDataMap, AddressTypeConfig } from './types';

const LOC_OPC_REGEX = /^(LOC|OPC)\d{12}$/i;

const isValidNmiOrMirnResponse = (data: unknown): data is IOriginAddressMeter => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'addressLabel' in data &&
    typeof data.addressLabel === 'string'
  );
};

const isSearchAddressResponse = (
  data: unknown
): data is {
  address: string;
  addressId: string;
  structuredAddress: SalesStructuredAddress;
} =>
  typeof data === 'object' &&
  data !== null &&
  'address' in data &&
  typeof data.address === 'string' &&
  'addressId' in data &&
  typeof data.addressId === 'string' &&
  'structuredAddress' in data &&
  typeof data.structuredAddress === 'object' &&
  data.structuredAddress !== null;

export const ADDRESS_TYPE_CONTENT_MAP: Record<AddressType, string> = {
  NMI: "You can search for your property using the street address details or the NMI number (found on the property's electricity / gas bill)",
  MIRN: "You can search for your property using the street address details or the MIRN number (found on the property's electricity / gas bill)",
  LOC_OPC:
    'You can search for your property using the street address details or the LOCid/OPCid number',
};

export const generateDialogContent = (enabledTypes: AddressType[]) => {
  if (enabledTypes.length === 0) {
    return 'Enter your exact property details below';
  }

  if (enabledTypes.length === 1) {
    return ADDRESS_TYPE_CONTENT_MAP[enabledTypes[0]];
  }

  // if enabledTypes contains both NMI and MIRN only, return a new content
  if (
    enabledTypes.includes(AddressTypes.NMI) &&
    enabledTypes.includes(AddressTypes.MIRN) &&
    enabledTypes.length === 2
  ) {
    return "You can search for your property using the street address details or the NMI / MIRN number (found on the property's electricity / gas bill)";
  }
};

export const useAddressTypeConfig = () => {
  return useMemo(
    (): {
      [K in AddressType]: AddressTypeConfig<AddressDataMap[K]>;
    } => ({
      NMI: {
        label: 'NMI number',
        maxLength: 11,
        validate: (value) => {
          if (!value) return 'Please enter your NMI number';
          if (value.length < 10 || value.length > 11)
            return 'NMI number must be either 10 or 11 characters long';
          return null;
        },
        buildUrl: (endpoint, value) => `${endpoint}/nmiOrMirn?nmi=${stripNMIChecksum(value)}`,
        extractData: (res) => {
          if (isValidNmiOrMirnResponse(res.data)) {
            return res.data;
          }
          return null;
        },
        searchComponent: AddressNMISearch,
      },
      MIRN: {
        label: 'MIRN number',
        maxLength: 11,
        validate: (value) => {
          if (!value) return 'Please enter your MIRN number';
          if (value.length < 10 || value.length > 11)
            return 'MIRN number must be either 10 or 11 characters long';
          return null;
        },
        buildUrl: (endpoint, value) => `${endpoint}/nmiOrMirn?mirn=${value}`,
        extractData: (res) => {
          if (isValidNmiOrMirnResponse(res.data)) {
            return res.data;
          }
          return null;
        },
        searchComponent: AddressMIRNSearch,
      },
      LOC_OPC: {
        label: 'LOCid/OPCid number',
        maxLength: 15,
        validate: (value) => {
          if (!LOC_OPC_REGEX.test(value)) return 'Invalid LOC/OPC format. Example: LOC123456789012';
          return null;
        },
        buildUrl: (endpoint, value) => `${endpoint}/addresses/${value}`,
        extractData: (res): IOriginAddressInternet | null => {
          if (isSearchAddressResponse(res.data)) {
            return {
              addressLabel: res.data.address,
              addressId: res.data.addressId,
              structuredAddress: res.data.structuredAddress,
            };
          }
          return null;
        },
        searchComponent: AddressLOCSearch,
      },
    }),
    []
  );
};
